// The cardholder's own card (/me/cards/{id}/*), in the shapes the legacy pages read (CardAccount, Transaction,
// ActionResult). A card is named by its id in the API: that is the "reference" the pages pass around.
import { amount, api, BffError, pascal, seg } from './bff';

/** CardAccountResponse */
export interface CardAccount {
  isConnected: boolean;
  message: string;
  displayName: string | null;
  email: string | null;
  availableBalance: number | null;
  ledgerBalance: number | null;
  currency: string;
  cardLastFour: string | null;
  cardStatus: string | null;
  cardReferenceId: string | null;
  /** What this card's processor offers beyond the common actions (PIN page, card image…); all false when unknown. */
  capabilities: CardCapabilities;
}

/** The API's `capabilities` of a card (docs/PARTNER_API.md § 4.3). */
export interface CardCapabilities {
  ssnRequired: boolean;
  kycQuestions: boolean;
  unload: boolean;
  reverse: boolean;
  closeCard: boolean;
  pinWidget: boolean;
  cardImage: boolean;
}

export const NO_CAPABILITIES: CardCapabilities = {
  ssnRequired: false,
  kycQuestions: false,
  unload: false,
  reverse: false,
  closeCard: false,
  pinWidget: false,
  cardImage: false,
};

/** A short-lived page the processor hosts (PIN widget, card image). */
export interface SecureLink {
  url: string;
  expiresAt: string | null;
}

/**
 * The line that names the card: its last four digits ("•••• 5104"). InterCash gives those once, when a card is issued,
 * and never for a replacement card; a card without them is named by InterCash's own identifier for it (the SAN, which
 * is not the number on the card and cannot pay for anything), its last four digits only: "Card ID •••• 8824". W26.
 */
export function cardNumberLine(
  account: Pick<CardAccount, 'cardLastFour' | 'cardReferenceId'>,
  provider: string | null | undefined,
): { text: string; isReference: boolean } {
  if (account.cardLastFour) return { text: `\u2022\u2022\u2022\u2022 ${account.cardLastFour}`, isReference: false };
  if (provider === 'InterCash' && account.cardReferenceId)
    return { text: `Card ID \u2022\u2022\u2022\u2022 ${account.cardReferenceId.slice(-4)}`, isReference: true };
  return { text: '\u2022\u2022\u2022\u2022 ----', isReference: false };
}

/** GET …/limits (D35): what the card may still be loaded with today; `supported` only for InterCash. */
export interface CardLimits {
  supported: boolean;
  message: string | null;
  currency: string | null;
  availableBalance: number | null;
  amountLeftToLoad: number | null;
  dailyLoadLimit: number | null;
  dailyLoadFrequencyLimit: number | null;
  loadsLeft: number | null;
}

/** ProviderTransactionSummary */
export interface Transaction {
  providerTransactionReference: string | null;
  transactionType: string;
  description: string;
  amount: number;
  currency: string;
  status: string;
  postedAt: string | null;
}

/** ProfileResponse (the address only on reads) */
export interface Profile {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  stateOrProvince?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

/** ProviderActionResult */
export interface ActionResult {
  success: boolean;
  message: string;
  providerReference: string | null;
}

interface LiveCard {
  id: string;
  last4: string | null;
  status: string;
  currency: string;
  availableBalance: string | null;
  ledgerBalance: string | null;
  stale: boolean;
  capabilities?: CardCapabilities;
}

interface ApiTransaction {
  reference: string | null;
  type: string;
  description: string;
  amount: string;
  currency: string;
  status: string;
  postedAt: string | null;
}

interface MeProfile {
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
}

/** A read that fails is "nothing" (null / no rows), as the legacy pages saw it. */
async function orNull<T>(call: Promise<T>): Promise<T | null> {
  try {
    return await call;
  } catch (err) {
    if (err instanceof BffError) return null;
    throw err;
  }
}

/** A refused action carries the API's own words ("This card is closed."). */
export async function action<T extends { success: boolean; message: string }>(call: Promise<T>): Promise<T> {
  try {
    return (await call) ?? ({ success: false, message: 'No response from API.' } as T);
  } catch (err) {
    if (err instanceof BffError) return { success: false, message: err.message } as T;
    throw err;
  }
}

export const toCardAccount = (
  c: LiveCard,
  holder: { displayName: string | null; email: string | null },
): CardAccount => ({
  isConnected: true,
  message: c.stale ? 'Could not reach the card processor; this is the last known balance.' : '',
  displayName: holder.displayName,
  email: holder.email,
  availableBalance: amount(c.availableBalance),
  ledgerBalance: amount(c.ledgerBalance),
  currency: c.currency,
  cardLastFour: c.last4,
  cardStatus: pascal(c.status),
  cardReferenceId: c.id,
  capabilities: { ...NO_CAPABILITIES, ...c.capabilities },
});

export const toTransaction = (t: ApiTransaction): Transaction => ({
  providerTransactionReference: t.reference,
  transactionType: pascal(t.type) ?? 'Unknown',
  description: t.description,
  amount: Number(t.amount),
  currency: t.currency,
  status: t.status,
  postedAt: t.postedAt,
});

const card = (ref: string, rest = '') => `/me/cards/${seg(ref)}${rest}`;

export const cardApi = {
  account: (ref: string, _cardReferenceId?: string | null) =>
    orNull(api<LiveCard>('GET', card(ref)).then((c) => toCardAccount(c, { displayName: null, email: null }))),
  transactions: async (ref: string, _cardReferenceId: string) =>
    ((await orNull(api<ApiTransaction[]>('GET', card(ref, '/transactions')))) ?? []).map(toTransaction),
  profile: (_ref: string) =>
    orNull(
      api<MeProfile>('GET', '/me/profile').then((p): Profile => ({
        firstName: p.firstName,
        lastName: p.lastName,
        email: p.email,
        phoneNumber: p.phone,
      })),
    ),
  /** The e-mail is the sign-in: changing it is support's (the API keeps it), so a changed one is refused here. */
  updateProfile: async (_ref: string, body: Profile): Promise<ActionResult> => {
    const current = await orNull(api<MeProfile>('GET', '/me/profile'));
    if (current && body.email.trim().toLowerCase() !== current.email)
      return {
        success: false,
        message: 'Your e-mail is how you sign in. Contact support to change it.',
        providerReference: null,
      };
    return action(
      api('PATCH', '/me/profile', {
        body: { firstName: body.firstName, lastName: body.lastName, phone: body.phoneNumber || null },
      }).then(() => ({ success: true, message: 'Your profile has been updated.', providerReference: null })),
    );
  },
  /** The processor's PIN page for this card (where offered): open it, it expires soon. */
  pinWidget: (ref: string) => action(api<SecureLink>('POST', card(ref, '/pin-widget')).then(linkResult)),
  /** The processor's image of the card, number included (where offered): never through QOR's own answers. */
  cardImage: (ref: string) => action(api<SecureLink>('GET', card(ref, '/display')).then(linkResult)),
  cardAction: (ref: string, name: CardAction, _userCardId: string) =>
    action(
      api<{ message: string }>('POST', card(ref, `/${name}`)).then((r): ActionResult => ({
        success: true,
        message: r.message,
        providerReference: null,
      })),
    ),
};

export type CardAction = 'activate' | 'freeze' | 'unfreeze' | 'reissue' | 'convert-to-physical';

const linkResult = (link: SecureLink) => ({ success: true, message: '', url: link.url, expiresAt: link.expiresAt });
